import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, Filter, Loader2, Building2, ExternalLink } from "lucide-react";
import { StoreHeader } from "../components/StoreHeader";
import { useAuth } from "../context/AuthContext";

/**
 * Vinci · National Brand Directory · A–Z Index.
 *
 * Renders the ~200-entry ``brands`` collection (cement + steel) as a
 * searchable, region-filterable, A–Z-jumpable grid. Gated to active
 * partners only via <ActiveOnlyRoute> in the App router.
 */

const MATERIALS = [
  { key: "all",    label: "All materials" },
  { key: "cement", label: "Cement" },
  { key: "tmt",    label: "Steel / TMT" },
];

const REGIONS = [
  { key: "all",      label: "Pan-India" },
  { key: "national", label: "National big-caps" },
  { key: "north",    label: "North" },
  { key: "south",    label: "South" },
  { key: "east",     label: "East / NE" },
  { key: "west",     label: "West" },
  { key: "central",  label: "Central" },
];

const ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

function BrandCard({ b }) {
  const initial = (b.name || "?").trim()[0].toUpperCase();
  const monogram = (b.name || "?").trim().split(/\s+/).slice(0, 2).map(w => w[0]).join("").toUpperCase() || "?";
  // Deterministic obsidian tint per slug so every brand tile stays visually stable.
  const palette = ["#0F1E2C","#1E1010","#0D1E1E","#101C10","#1A1010","#161014","#0A1620"];
  const tint = palette[[...(b.slug || "x")].reduce((s, ch) => s + ch.charCodeAt(0), 0) % palette.length];
  return (
    <motion.a
      data-testid={`brand-card-${b.slug}`}
      href={b.domain ? `https://${b.domain}` : "#"}
      target={b.domain ? "_blank" : undefined}
      rel="noreferrer"
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -3 }}
      className="group border border-white/10 rounded-sm bg-[#0a0a0a] p-4 hover:border-[#B87333]/50 transition-colors flex items-center gap-3"
    >
      <div className="w-14 h-14 shrink-0 rounded-sm relative border border-[#D8B23E]/30 overflow-hidden flex items-center justify-center"
           style={{ background: tint }}>
        {/* Monogram sits underneath — always visible; Clearbit img is stacked on top and hides only on load. */}
        <span className="font-serif text-xl text-[#D8B23E] tracking-tight">{monogram}</span>
        {b.logo_url && (
          <img src={b.logo_url} alt={b.name}
               className="absolute inset-0 w-full h-full object-contain bg-white/5"
               onError={(e) => { e.currentTarget.style.display = "none"; }} />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-serif text-base text-[#F5F5F0] truncate">{b.name}</div>
        <div className="font-body text-[11px] text-[#8A8A82] uppercase tracking-[0.16em] mt-0.5">
          {b.material === "cement" ? "Cement" : "Steel · TMT"} · {b.region}
        </div>
      </div>
      {b.domain && (
        <ExternalLink size={13} className="text-[#8A8A82] group-hover:text-[#B87333] shrink-0" />
      )}
    </motion.a>
  );
}

export default function BrandsIndex() {
  const { api, user } = useAuth();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [material, setMaterial] = useState("all");
  const [region, setRegion] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (material !== "all") params.set("material", material);
    if (region !== "all") params.set("region", region);
    if (q) params.set("q", q);
    api.get(`/brands?${params.toString()}`)
       .then((r) => { if (alive) setBrands(r.data || []); })
       .catch(() => { if (alive) setBrands([]); })
       .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api, material, region, q]);

  const byLetter = useMemo(() => {
    const out = {};
    for (const b of brands) {
      const k = (b.name || "?").trim()[0].toUpperCase();
      (out[k] = out[k] || []).push(b);
    }
    return out;
  }, [brands]);

  const activeLetters = new Set(Object.keys(byLetter));

  if (user === null) return <div className="min-h-screen bg-[#070707]" />;

  return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0]">
      <StoreHeader />

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#B87333]">Vinci · National Directory</div>
          <h1 className="font-serif text-5xl md:text-6xl tracking-[-0.02em] mt-2">Brand Ecosystem</h1>
          <p className="font-body text-[#8A8A82] mt-4 max-w-2xl">
            Every cement &amp; steel-TMT brand Vinci works with — 100+ mills across all six Indian tiers,
            with live rate history from the Price Pulse desk. Tap a brand to visit their site.
          </p>
        </motion.div>

        {/* Filter bar */}
        <div className="mt-10 flex flex-wrap gap-3 items-center border-t border-b border-white/10 py-5">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A82]" />
            <input data-testid="brand-search"
                   value={q}
                   onChange={(e) => setQ(e.target.value)}
                   placeholder="Search UltraTech, Tata Tiscon, Ramco…"
                   className="w-full bg-black/40 border border-white/15 rounded-sm pl-9 pr-3 py-2.5 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {MATERIALS.map((m) => (
              <button key={m.key} data-testid={`brand-material-${m.key}`}
                      onClick={() => setMaterial(m.key)}
                      className={`px-3 py-1.5 rounded-full text-[10px] font-grotesk uppercase tracking-[0.2em] border transition-colors ${material === m.key ? "border-[#B87333] text-[#B87333] bg-[#B87333]/10" : "border-white/15 text-[#8A8A82] hover:border-white/30"}`}>
                {m.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <Filter size={12} className="text-[#8A8A82] self-center mr-1" />
            {REGIONS.map((r) => (
              <button key={r.key} data-testid={`brand-region-${r.key}`}
                      onClick={() => setRegion(r.key)}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-grotesk uppercase tracking-[0.2em] border transition-colors ${region === r.key ? "border-[#D8B23E] text-[#D8B23E] bg-[#D8B23E]/10" : "border-white/15 text-[#8A8A82] hover:border-white/30"}`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* A–Z jump nav */}
        <div className="mt-6 flex flex-wrap gap-1 text-[11px]">
          {ALPHA.map((ch) => (
            <a key={ch} href={`#letter-${ch}`}
               data-testid={`brand-jump-${ch}`}
               className={`px-2 py-1 rounded-sm font-grotesk uppercase tracking-[0.14em] transition-colors ${activeLetters.has(ch) ? "text-[#F5F5F0] bg-white/[0.05] hover:bg-[#B87333] hover:text-black" : "text-[#3a3a3a] pointer-events-none"}`}>
              {ch}
            </a>
          ))}
        </div>

        <div className="mt-3 font-body text-xs text-[#8A8A82]">
          {loading ? <><Loader2 size={11} className="inline animate-spin mr-1.5" />Loading Vinci directory…</>
                   : `${brands.length} brand${brands.length === 1 ? "" : "s"} indexed`}
        </div>

        {/* A-Z grouped grid */}
        <div className="mt-6 space-y-10">
          {ALPHA.filter((ch) => byLetter[ch]).map((ch) => (
            <section key={ch} id={`letter-${ch}`} data-testid={`brand-letter-${ch}`}>
              <div className="flex items-baseline gap-3 mb-3">
                <div className="font-serif text-4xl text-[#D8B23E]">{ch}</div>
                <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">
                  {byLetter[ch].length} {byLetter[ch].length === 1 ? "brand" : "brands"}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {byLetter[ch].map((b) => <BrandCard key={b.slug} b={b} />)}
              </div>
            </section>
          ))}
        </div>

        {!loading && brands.length === 0 && (
          <div className="mt-10 border border-white/10 rounded-sm p-10 text-center">
            <Building2 size={22} className="mx-auto text-[#8A8A82]" />
            <div className="mt-3 font-body text-sm text-[#8A8A82]">No brand matches those filters. Try widening the search.</div>
          </div>
        )}
      </div>
    </div>
  );
}
